import { z } from 'zod';
import { idSchema, timestampsSchema } from './common';

/** Routing rules attached to a taxonomy label. */
export const taxonomyRoutingSchema = z
  .object({
    /** Keywords used by the rule-based classifier (case-insensitive substring match). */
    keywords: z.array(z.string()).default([]),
    /** Orchestrator/specialist agent to route matching intake to. */
    targetAgentId: idSchema.optional(),
    /** Higher priority wins when multiple labels match equally. */
    priority: z.number().int().default(0),
  })
  .passthrough();
export type TaxonomyRouting = z.infer<typeof taxonomyRoutingSchema>;

export const taxonomyLabelSchema = z
  .object({
    id: idSchema,
    tenantId: idSchema,
    workspaceId: idSchema,
    key: z
      .string()
      .min(1)
      .max(80)
      .regex(/^[a-z0-9_.-]+$/, 'key must be kebab/snake case'),
    name: z.string().min(1).max(200),
    description: z.string().max(2000).default(''),
    routing: taxonomyRoutingSchema.default({}),
  })
  .merge(timestampsSchema);
export type TaxonomyLabel = z.infer<typeof taxonomyLabelSchema>;

export const createTaxonomyLabelSchema = taxonomyLabelSchema.pick({
  key: true,
  name: true,
  description: true,
  routing: true,
});
export type CreateTaxonomyLabel = z.infer<typeof createTaxonomyLabelSchema>;

export const updateTaxonomyLabelSchema = createTaxonomyLabelSchema.partial();
export type UpdateTaxonomyLabel = z.infer<typeof updateTaxonomyLabelSchema>;

export const replaceTaxonomySchema = z.array(createTaxonomyLabelSchema);
export type ReplaceTaxonomy = z.infer<typeof replaceTaxonomySchema>;

export function parseTaxonomyJson(raw: string): { labels: CreateTaxonomyLabel[]; error?: string } {
  try {
    const parsed = JSON.parse(raw) as unknown;
    const labels = replaceTaxonomySchema.parse(parsed);
    return { labels };
  } catch (err) {
    return { labels: [], error: err instanceof Error ? err.message : 'Invalid taxonomy JSON' };
  }
}
